import { Link } from '@inertiajs/react';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// Types matching the backend serializer
interface Author {
  id: number;
  name: string;
  avatarUrl: string | null;
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

interface Tag {
  id: number;
  name: string;
  slug: string;
}

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  coverImageUrl: string | null;
  publishedAt: string | null;
  readingTimeMinutes: number;
  visibility: 'visible' | 'auth_only';
  status: string;
  author: Author | null;
  categories: Category[];
  tags: Tag[];
}

interface BlogPostCardProps {
  post: BlogPost;
  variant?: 'default' | 'featured';
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}

// Default cover image
const DEFAULT_COVER_IMAGE = '/images/mind-connect.webp';

/**
 * BlogPostCard - A reusable card component for displaying blog post previews
 * 
 * Features:
 * - Cover image with 16:9 aspect ratio
 * - Title, summary, author, date, reading time
 * - Category and tag badges
 * - Visibility badge for auth-only posts
 * - Supports both default and featured variants
 */
export function BlogPostCard({
  post,
  variant = 'default',
  className,
  onClick,
}: BlogPostCardProps) {
  const isAuthOnly = post.visibility === 'auth_only';
  const coverImage = post.coverImageUrl || DEFAULT_COVER_IMAGE;

  const formattedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  if (variant === 'featured') {
    return (
      <Card className={cn('overflow-hidden hover:shadow-md transition-shadow', className)}>
        {/* Cover Image */}
        <Link href={`/blog/${post.slug}`} className="block">
          <div className="relative overflow-hidden bg-muted h-48">
            <img
              src={coverImage}
              alt={post.title}
              className="w-full h-full object-contain"
            />
          </div>
        </Link>

        <CardHeader className="pb-2">
          {/* Badges */}
          <div className="flex flex-wrap gap-1 mb-2">
            {isAuthOnly && <MembersOnlyBadge />}
            {post.categories.slice(0, 1).map((category) => (
              <Badge key={category.id} variant="secondary" className="hover:bg-secondary/80">
                <Link href={`/blog/category/${category.slug}`}>
                  {category.name}
                </Link>
              </Badge>
            ))}
          </div>

          {/* Tags */}
          {post.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {post.tags.slice(0, 3).map((tag) => (
                <Badge key={tag.id} variant="outline" className="hover:bg-muted">
                  <Link href={`/blog/tag/${tag.slug}`}>#{tag.name}</Link>
                </Badge>
              ))}
              {post.tags.length > 3 && (
                <span className="text-xs text-muted-foreground py-1">
                  +{post.tags.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Title */}
          <Link href={`/blog/${post.slug}`} onClick={onClick}>
            <CardTitle className="text-2xl hover:text-primary transition-colors">
              {post.title}
            </CardTitle>
          </Link>

          {/* Description */}
          <CardDescription className="line-clamp-3">{post.summary}</CardDescription>
        </CardHeader>

        <CardContent className="p-0"></CardContent>

        <CardFooter className="flex items-center justify-between text-xs text-muted-foreground pt-2">
          <div className="flex items-center space-x-2">
            {post.author && (
              <>
                <span className="font-medium">{post.author.name}</span>
                <span>•</span>
              </>
            )}
            <span>{formattedDate}</span>
          </div>
          <span>{post.readingTimeMinutes} min read</span>
        </CardFooter>
      </Card>
    );
  }

  // Default variant (compact)
  return (
    <Card className={cn('overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col', className)}>
      {/* Cover Image */}
      <Link href={`/blog/${post.slug}`} className="block">
        <div className="relative overflow-hidden bg-muted h-40">
          <img
            src={coverImage}
            alt={post.title}
            className="w-full h-full object-contain"
          />
        </div>
      </Link>

      <CardHeader className="flex-1 pb-2">
        {/* Badges */}
        <div className="flex flex-wrap gap-1 mb-1">
          {post.categories.slice(0, 1).map((category) => (
            <Badge key={category.id} variant="secondary" className="text-xs hover:bg-secondary/80">
              <Link href={`/blog/category/${category.slug}`}>
                {category.name}
              </Link>
            </Badge>
          ))}
        </div>

        {/* Tags */}
        {post.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {post.tags.slice(0, 2).map((tag) => (
              <Badge key={tag.id} variant="outline" className="text-xs hover:bg-muted">
                <Link href={`/blog/tag/${tag.slug}`}>#{tag.name}</Link>
              </Badge>
            ))}
            {post.tags.length > 2 && (
              <span className="text-xs text-muted-foreground py-1">
                +{post.tags.length - 2}
              </span>
            )}
          </div>
        )}

        {/* Title */}
        <Link href={`/blog/${post.slug}`} onClick={onClick}>
          <CardTitle className="text-lg hover:text-primary transition-colors line-clamp-2">
            {post.title}
          </CardTitle>
        </Link>

        {/* Description */}
        <CardDescription className="line-clamp-2">{post.summary}</CardDescription>
      </CardHeader>

      <CardContent className="p-0"></CardContent>

      <CardFooter className="flex items-center justify-between text-xs text-muted-foreground pt-1 mt-auto">
        <div className="flex items-center space-x-2">
          {post.author && (
            <>
              <span className="font-medium">{post.author.name}</span>
              <span>•</span>
            </>
          )}
          <span>{formattedDate}</span>
          {isAuthOnly && <MembersOnlyBadge />}
        </div>
        <span>{post.readingTimeMinutes} min</span>
      </CardFooter>
    </Card>
  );
}

// Members Only Badge Component
function MembersOnlyBadge() {
  return (
    <span className="inline-flex items-center gap-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-medium px-2 py-1 rounded-full">
      <svg
        className="w-3 h-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
      Members
    </span>
  );
}

export default BlogPostCard;
